import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CombineAnimation from './CombineAnimation';

const cornPriceData = [
  { year: '2020', feb: 100, mar: 109, apr: 117, may: 126 },
  { year: '2021', feb: 100, mar: 112, apr: 124, may: 138 },
  { year: '2022', feb: 100, mar: 114, apr: 121, may: 133 },
  { year: '2023', feb: 100, mar: 107, apr: 115, may: 128 },
  { year: '2024', feb: 100, mar: 110, apr: 119, may: 131 },
];

const CHART_W = 480;
const CHART_H = 220;
const PAD_L = 40;
const PAD_B = 32;
const PAD_T = 16;
const PAD_R = 16;
const colors = ['#22c55e', '#4ade80', '#86efac', '#bbf7d0', '#dcfce7'];

function dataToPoints(row: typeof cornPriceData[0]) {
  const vals = [row.feb, row.mar, row.apr, row.may];
  const minV = 95, maxV = 145;
  return vals.map((v, i) => {
    const x = PAD_L + (i / 3) * (CHART_W - PAD_L - PAD_R);
    const y = CHART_H - PAD_B - ((v - minV) / (maxV - minV)) * (CHART_H - PAD_B - PAD_T);
    return { x, y, v };
  });
}

const HeroChart = () => {
  const { t } = useTranslation();
  const months = [t('hero.months.0'), t('hero.months.1'), t('hero.months.2'), t('hero.months.3')];
  const [progress, setProgress] = useState(0);
  const [hovered, setHovered] = useState<{ x: number; y: number; v: number; year: string } | null>(null);
  const animRef = useRef<number>(0);

  useEffect(() => {
    let start: number | null = null;
    const duration = 1800;
    const animate = (ts: number) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setProgress(p);
      if (p < 1) animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animRef.current);
  }, []);

  const minV = 95, maxV = 145;
  const yGridLines = [100, 110, 120, 130, 140];

  return (
    <div className="relative w-full" style={{ maxWidth: CHART_W }}>
      <svg
        viewBox={`0 0 ${CHART_W} ${CHART_H}`}
        className="w-full"
        style={{ fontFamily: 'inherit' }}
      >
        {/* Grid */}
        {yGridLines.map(val => {
          const y = CHART_H - PAD_B - ((val - minV) / (maxV - minV)) * (CHART_H - PAD_B - PAD_T);
          return (
            <g key={val}>
              <line x1={PAD_L} y1={y} x2={CHART_W - PAD_R} y2={y} stroke="rgba(255,255,255,0.07)" strokeWidth="1" />
              <text x={PAD_L - 6} y={y + 4} fill="rgba(255,255,255,0.35)" fontSize="10" textAnchor="end">{val}%</text>
            </g>
          );
        })}

        {/* Month labels */}
        {months.map((m, i) => {
          const x = PAD_L + (i / 3) * (CHART_W - PAD_L - PAD_R);
          return (
            <text key={m} x={x} y={CHART_H - 6} fill="rgba(255,255,255,0.4)" fontSize="11" textAnchor="middle">{m}</text>
          );
        })}

        {/* Lines */}
        {cornPriceData.map((row, ri) => {
          const pts = dataToPoints(row);
          const totalLen = pts.slice(1).reduce((sum, p, i) => {
            const prev = pts[i];
            return sum + Math.hypot(p.x - prev.x, p.y - prev.y);
          }, 0);
          const drawLen = totalLen * progress;

          let pathD = `M ${pts[0].x} ${pts[0].y}`;
          let accumulated = 0;
          for (let i = 1; i < pts.length; i++) {
            const seg = Math.hypot(pts[i].x - pts[i-1].x, pts[i].y - pts[i-1].y);
            if (accumulated >= drawLen) break;
            if (accumulated + seg <= drawLen) {
              pathD += ` L ${pts[i].x} ${pts[i].y}`;
            } else {
              const t = (drawLen - accumulated) / seg;
              const mx = pts[i-1].x + t * (pts[i].x - pts[i-1].x);
              const my = pts[i-1].y + t * (pts[i].y - pts[i-1].y);
              pathD += ` L ${mx} ${my}`;
            }
            accumulated += seg;
          }

          return (
            <path
              key={row.year}
              d={pathD}
              fill="none"
              stroke={colors[ri]}
              strokeWidth={ri === 1 ? 2.5 : 1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={ri === 1 ? 1 : 0.5}
            />
          );
        })}

        {/* Interactive dots (latest year highlight) */}
        {progress >= 1 && cornPriceData.map((row, ri) => {
          const pts = dataToPoints(row);
          return pts.map((p, pi) => (
            <circle
              key={`${ri}-${pi}`}
              cx={p.x} cy={p.y} r={ri === 1 ? 4 : 3}
              fill={colors[ri]}
              opacity={ri === 1 ? 1 : 0.5}
              className="cursor-pointer"
              onMouseEnter={() => setHovered({ x: p.x, y: p.y, v: p.v, year: row.year })}
              onMouseLeave={() => setHovered(null)}
            />
          ));
        })}

        {/* Tooltip */}
        {hovered && (
          <g>
            <rect x={hovered.x - 32} y={hovered.y - 38} width={64} height={22} rx="5" fill="#1a1d24" stroke="#22c55e" strokeWidth="1" />
            <text x={hovered.x} y={hovered.y - 22} fill="#22c55e" fontSize="11" textAnchor="middle" fontWeight="bold">
              {hovered.year}: {hovered.v}%
            </text>
          </g>
        )}
      </svg>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-2 pl-10">
        {cornPriceData.map((row, ri) => (
          <div key={row.year} className="flex items-center gap-1.5">
            <span className="w-5 h-0.5 inline-block" style={{ backgroundColor: colors[ri], opacity: ri === 1 ? 1 : 0.5 }}></span>
            <span className="text-xs" style={{ color: colors[ri], opacity: ri === 1 ? 1 : 0.6 }}>{row.year}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const HeroSection = () => {
  const { t } = useTranslation();
  const scrollToCalc = () => {
    const el = document.getElementById('calculator');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden" style={{ background: 'linear-gradient(135deg, #0d1117 0%, #111418 50%, #0a1a0e 100%)' }}>
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-96 h-96 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #22c55e, transparent)' }}></div>
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #16a34a, transparent)' }}></div>
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.04) 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-20 md:pt-24 pb-36 md:pb-48 w-full">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Left */}
          <div className="flex-1 w-full">
            <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              <span className="text-green-400 text-xs font-semibold tracking-wide uppercase">{t('hero.badge')}</span>
            </div>

            <h1 className="text-4xl md:text-5xl xl:text-6xl font-black text-white leading-tight mb-6">
              {t('hero.title_p1')}{' '}
              <span className="text-green-400">{t('hero.title_hl')}</span>{' '}
              {t('hero.title_p2')}
            </h1>

            <p className="text-white/60 text-base md:text-lg leading-relaxed mb-8 max-w-xl">
              {t('hero.desc')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={scrollToCalc}
                className="flex items-center justify-center gap-3 bg-green-500 hover:bg-green-400 text-white font-bold px-8 py-4 rounded-full transition-all duration-200 hover:-translate-y-0.5 cursor-pointer whitespace-nowrap text-sm md:text-base"
              >
                <span>{t('hero.cta1')}</span>
                <span className="w-7 h-7 flex items-center justify-center bg-white/20 rounded-full">
                  <i className="ri-arrow-right-line text-sm"></i>
                </span>
              </button>
              <button
                onClick={() => document.getElementById('packages')?.scrollIntoView({ behavior: 'smooth' })}
                className="flex items-center justify-center gap-2 border border-white/20 text-white/70 hover:text-white hover:border-white/40 font-semibold px-8 py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap text-sm md:text-base"
              >
                <i className="ri-bar-chart-2-line"></i>
                <span>{t('hero.cta2')}</span>
              </button>
            </div>

            {/* Stats row */}
            <div className="flex flex-wrap gap-6 mt-10">
              {[
                { value: t('hero.stat1.val'), label: t('hero.stat1.label') },
                { value: t('hero.stat2.val'), label: t('hero.stat2.label') },
                { value: t('hero.stat3.val'), label: t('hero.stat3.label') },
              ].map(s => (
                <div key={s.value}>
                  <div className="text-green-400 font-black text-2xl">{s.value}</div>
                  <div className="text-white/40 text-xs mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Chart */}
          <div className="flex-1 w-full max-w-lg">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-white font-semibold text-sm">{t('hero.chart.title')}</div>
                  <div className="text-white/40 text-xs mt-0.5">{t('hero.chart.subtitle')}</div>
                </div>
                <div className="flex items-center gap-1.5 bg-green-500/10 border border-green-500/20 rounded-full px-3 py-1">
                  <i className="ri-arrow-up-line text-green-400 text-xs"></i>
                  <span className="text-green-400 text-xs font-semibold">+31% avg</span>
                </div>
              </div>
              <HeroChart />
            </div>

            {/* Mini info */}
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                <div className="text-green-400 font-bold text-lg">{t('hero.chart.maxgrowth')}</div>
                <div className="text-white/50 text-xs">{t('hero.chart.maxlabel')}</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                <div className="text-green-400 font-bold text-lg">{t('hero.chart.years')}</div>
                <div className="text-white/50 text-xs">{t('hero.chart.yearslabel')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Combine harvester animation strip ── */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        <div className="w-full h-px bg-gradient-to-r from-transparent via-green-500/25 to-transparent" />
        <CombineAnimation />
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-28 md:bottom-44 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 animate-bounce z-20 pointer-events-none">
        <span className="text-white/25 text-xs hidden md:block">{t('hero.scroll')}</span>
        <i className="ri-arrow-down-line text-white/25 text-base"></i>
      </div>
    </section>
  );
};

export default HeroSection;
